import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, Save, X, CheckCircle, ListTodo, Flag } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { API } from '../config/api';

function AdminSettings({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('statuses');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [statuses, setStatuses] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [milestones, setMilestones] = useState([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    window.location.href = '/login';
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statusesRes, templatesRes, milestonesRes] = await Promise.all([
        fetch(`${API}/admin/settings/statuses`, { credentials: 'include' }),
        fetch(`${API}/admin/settings/task-templates`, { credentials: 'include' }),
        fetch(`${API}/admin/settings/milestones`, { credentials: 'include' })
      ]);

      if (statusesRes.ok) {
        const data = await statusesRes.json();
        setStatuses(data.statuses || []);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTaskTemplates(data.templates || []);
      }
      if (milestonesRes.ok) {
        const data = await milestonesRes.json();
        setMilestones(data.milestones || []);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Open modal for creating/editing
  const openModal = (type, item = null) => {
    setEditingItem(item);
    if (type === 'status') {
      setFormData(item ? {
        value: item.value,
        label: item.label,
        description: item.description || ''
      } : {
        value: '',
        label: '',
        description: ''
      });
    } else if (type === 'template') {
      setFormData(item ? {
        case_type: item.case_type,
        category: item.category,
        title: item.title,
        description: item.description || '',
        why_needed: item.why_needed || ''
      } : {
        case_type: 'personal_injury',
        category: 'General',
        title: '',
        description: '',
        why_needed: ''
      });
    } else if (type === 'milestone') {
      setFormData(item ? {
        value: item.value,
        label: item.label,
        description: item.description || ''
      } : {
        value: '',
        label: '',
        description: ''
      });
    }
    setShowModal(true);
  };

  // Save item
  const handleSave = async () => {
    try {
      let endpoint, method, body;
      
      if (activeTab === 'statuses') {
        endpoint = editingItem 
          ? `${API}/admin/settings/statuses/${editingItem.status_id}`
          : `${API}/admin/settings/statuses`;
        method = editingItem ? 'PATCH' : 'POST';
        body = {
          value: formData.value || formData.label.toLowerCase().replace(/\s+/g, '_'),
          label: formData.label,
          description: formData.description,
          order: editingItem?.order || 0
        };
      } else if (activeTab === 'templates') {
        endpoint = editingItem 
          ? `${API}/admin/settings/task-templates/${editingItem.template_id}`
          : `${API}/admin/settings/task-templates`;
        method = editingItem ? 'PATCH' : 'POST';
        body = {
          case_type: formData.case_type,
          category: formData.category,
          title: formData.title,
          description: formData.description,
          why_needed: formData.why_needed,
          order: editingItem?.order || 0
        };
      } else if (activeTab === 'milestones') {
        endpoint = editingItem 
          ? `${API}/admin/settings/milestones/${editingItem.milestone_id}`
          : `${API}/admin/settings/milestones`;
        method = editingItem ? 'PATCH' : 'POST';
        body = {
          value: formData.value || formData.label.toLowerCase().replace(/\s+/g, '_'),
          label: formData.label,
          description: formData.description,
          order: editingItem?.order || 0
        };
      }

      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save');
      }

      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Delete item
  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      let endpoint;
      if (type === 'status') {
        endpoint = `${API}/admin/settings/statuses/${id}`;
      } else if (type === 'template') {
        endpoint = `${API}/admin/settings/task-templates/${id}`;
      } else if (type === 'milestone') {
        endpoint = `${API}/admin/settings/milestones/${id}`;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Group task templates by case type first, then by category
  const templatesByCaseType = {
    personal_injury: taskTemplates.filter(t => t.case_type === 'personal_injury'),
    employment: taskTemplates.filter(t => t.case_type === 'employment'),
    all: taskTemplates.filter(t => t.case_type === 'all')
  };

  // Helper to group templates by category within a case type
  const groupByCategory = (templates) => {
    return templates.reduce((acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc[template.category].push(template);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
              <p className="text-gray-500">Manage case statuses, task templates, and milestones</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('statuses')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'statuses'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="statuses-tab"
            >
              <CheckCircle className="h-4 w-4" />
              Case Statuses ({statuses.length})
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'templates'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="templates-tab"
            >
              <ListTodo className="h-4 w-4" />
              Task Templates ({taskTemplates.length})
            </button>
            <button
              onClick={() => setActiveTab('milestones')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'milestones'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="milestones-tab"
            >
              <Flag className="h-4 w-4" />
              Milestones ({milestones.length})
            </button>
          </nav>
        </div>

        {/* Statuses Tab */}
        {activeTab === 'statuses' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Case Statuses</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Define the stages a case can go through</p>
              </div>
              <Button
                onClick={() => openModal('status')}
                className="bg-[#80298F] hover:bg-[#6b2278]"
                data-testid="add-status-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Status
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {statuses.map((status, index) => (
                  <div
                    key={status.status_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-[#80298F]/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#80298F]/10 text-[#80298F] font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{status.label}</p>
                        <p className="text-sm text-gray-500">{status.value}</p>
                        {status.description && (
                          <p className="text-xs text-gray-400 mt-1 max-w-md truncate">{status.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openModal('status', status)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete('status', status.status_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Task Templates Tab */}
        {activeTab === 'templates' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Task Templates</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Preset tasks that can be added to cases</p>
              </div>
              <Button
                onClick={() => openModal('template')}
                className="bg-[#80298F] hover:bg-[#6b2278]"
                data-testid="add-template-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Template
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {/* Personal Injury Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <h3 className="text-lg font-semibold text-gray-900">Personal Injury</h3>
                    <span className="text-sm text-gray-500">({templatesByCaseType.personal_injury.length} templates)</span>
                  </div>
                  {templatesByCaseType.personal_injury.length > 0 ? (
                    <div className="space-y-4 ml-6">
                      {Object.entries(groupByCategory(templatesByCaseType.personal_injury)).map(([category, templates]) => (
                        <div key={`pi-${category}`}>
                          <p className="text-sm font-medium text-gray-600 mb-2">{category}</p>
                          <div className="space-y-2">
                            {templates.map((template) => (
                              <div
                                key={template.template_id}
                                className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                              >
                                <div>
                                  <p className="font-medium text-gray-900">{template.title}</p>
                                  {template.description && (
                                    <p className="text-sm text-gray-500">{template.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => openModal('template', template)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete('template', template.template_id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm ml-6">No templates for Personal Injury cases</p>
                  )}
                </div>

                {/* Employment Law Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <h3 className="text-lg font-semibold text-gray-900">Employment Law</h3>
                    <span className="text-sm text-gray-500">({templatesByCaseType.employment.length} templates)</span>
                  </div>
                  {templatesByCaseType.employment.length > 0 ? (
                    <div className="space-y-4 ml-6">
                      {Object.entries(groupByCategory(templatesByCaseType.employment)).map(([category, templates]) => (
                        <div key={`emp-${category}`}>
                          <p className="text-sm font-medium text-gray-600 mb-2">{category}</p>
                          <div className="space-y-2">
                            {templates.map((template) => (
                              <div
                                key={template.template_id}
                                className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                              >
                                <div>
                                  <p className="font-medium text-gray-900">{template.title}</p>
                                  {template.description && (
                                    <p className="text-sm text-gray-500">{template.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => openModal('template', template)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete('template', template.template_id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm ml-6">No templates for Employment Law cases</p>
                  )}
                </div>

                {/* All Cases Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
                    <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                    <h3 className="text-lg font-semibold text-gray-900">All Cases</h3>
                    <span className="text-sm text-gray-500">({templatesByCaseType.all.length} templates)</span>
                  </div>
                  {templatesByCaseType.all.length > 0 ? (
                    <div className="space-y-4 ml-6">
                      {Object.entries(groupByCategory(templatesByCaseType.all)).map(([category, templates]) => (
                        <div key={`all-${category}`}>
                          <p className="text-sm font-medium text-gray-600 mb-2">{category}</p>
                          <div className="space-y-2">
                            {templates.map((template) => (
                              <div
                                key={template.template_id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                              >
                                <div>
                                  <p className="font-medium text-gray-900">{template.title}</p>
                                  {template.description && (
                                    <p className="text-sm text-gray-500">{template.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => openModal('template', template)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete('template', template.template_id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm ml-6">No templates for All Cases</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Milestones Tab */}
        {activeTab === 'milestones' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Case Milestones</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Key events that can be marked on a case</p>
              </div>
              <Button
                onClick={() => openModal('milestone')}
                className="bg-[#80298F] hover:bg-[#6b2278]"
                data-testid="add-milestone-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Milestone
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.milestone_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-[#80298F]/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{milestone.label}</p>
                        <p className="text-sm text-gray-500">{milestone.value}</p>
                        {milestone.description && (
                          <p className="text-xs text-gray-400 mt-1 max-w-md truncate">{milestone.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openModal('milestone', milestone)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete('milestone', milestone.milestone_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit' : 'Add'} {
                activeTab === 'statuses' ? 'Status' : 
                activeTab === 'templates' ? 'Task Template' : 'Milestone'
              }
            </DialogTitle>
            <DialogDescription>
              {activeTab === 'statuses' 
                ? 'Configure a case status that will appear in the status dropdown.' 
                : activeTab === 'templates' 
                ? 'Create a task template that can be added to cases.'
                : 'Define a milestone event that can be marked on cases.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {activeTab === 'statuses' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
                  <Input
                    value={formData.label || ''}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g., Awaiting Documents"
                    data-testid="status-label-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <Input
                    value={formData.value || ''}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="Auto-generated from label"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate from label</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description for Clients</label>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What this status means for the client..."
                    rows={3}
                  />
                </div>
              </>
            )}

            {activeTab === 'templates' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case Type *</label>
                  <Select
                    value={formData.case_type || 'personal_injury'}
                    onValueChange={(value) => setFormData({ ...formData, case_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal_injury">Personal Injury</SelectItem>
                      <SelectItem value="employment">Employment Law</SelectItem>
                      <SelectItem value="all">All Case Types</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <Input
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Identity, Medical, Evidence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Task Title *</label>
                  <Input
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Upload copy of your government I.D."
                    data-testid="template-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Instructions for the client..."
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Why This Is Needed</label>
                  <Textarea
                    value={formData.why_needed || ''}
                    onChange={(e) => setFormData({ ...formData, why_needed: e.target.value })}
                    placeholder="Explain why this task is important for the case..."
                    rows={2}
                  />
                </div>
              </>
            )}

            {activeTab === 'milestones' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
                  <Input
                    value={formData.label || ''}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g., Demand Letter Sent"
                    data-testid="milestone-label-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <Input
                    value={formData.value || ''}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="Auto-generated from label"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate from label</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description shown in notifications..."
                    rows={3}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[#80298F] hover:bg-[#6b2278]"
              data-testid="save-item-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminSettings;
